import { FormattedMessage, formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import {
  Button,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { MemoButtonContained } from 'components/general/ButtonContained/ButtonContained';
import React, { useCallback } from 'react';
import useIsMobile from '../../../../hooks/useIsMobile';
import { ArrowDropDown } from '@mui/icons-material';
import { usePopover } from '../../../../hooks/usePopover';

function ButtonWithPopover(props: {
  posting: boolean;
  onSaveDraft: () => void;
  onPublish: () => void;
}): React.ReactElement {
  const { posting, onSaveDraft, onPublish } = props;
  const { attachPopover, closePopover, popoverOpen, anchorEl } =
    usePopover();

  const handleSaveDraftClick = useCallback(() => {
    closePopover();
    onSaveDraft();
  }, [closePopover, onSaveDraft]);

  const handlePublishClick = useCallback(() => {
    closePopover();
    onPublish();
  }, [closePopover, onPublish]);

  return (
    <>
      <MemoButtonContained
        onClick={attachPopover}
        endIcon={
          posting ? <CircularProgress size={16} /> : <ArrowDropDown />
        }
        disabled={posting}
      >
        <FormattedMessage id={CommonTranslationKeys.save} />
      </MemoButtonContained>
      <Menu open={popoverOpen} anchorEl={anchorEl} onClose={closePopover}>
        <MenuItem onClick={handleSaveDraftClick}>
          <ListItemText>
            <FormattedMessage id={CommonTranslationKeys.saveAsDraft} />
          </ListItemText>
        </MenuItem>
        <Divider sx={{ margin: '0px 16px' }} />
        <MenuItem onClick={handlePublishClick}>
          <ListItemText>
            <FormattedMessage id={CommonTranslationKeys.publish} />
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export function SubmitButtons(props: {
  onSaveDraft: () => void;
  saveDraftInProgress: boolean;
  publishing: boolean;
  onPublish: () => void;
}): React.ReactElement {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const { onSaveDraft, publishing, onPublish, saveDraftInProgress } =
    props;
  const posting = saveDraftInProgress || publishing;

  const mobileButton = (
    <ButtonWithPopover
      posting={posting}
      onSaveDraft={onSaveDraft}
      onPublish={onPublish}
    />
  );

  const desktopButtons = (
    <>
      <Button
        onClick={onSaveDraft}
        sx={{ marginRight: '19px' }}
        endIcon={
          saveDraftInProgress ? <CircularProgress size={16} /> : undefined
        }
        disabled={posting}
      >
        {formatTranslation(intl, CommonTranslationKeys.saveAsDraft)}
      </Button>
      <MemoButtonContained
        onClick={onPublish}
        endIcon={publishing ? <CircularProgress size={16} /> : undefined}
        disabled={posting}
      >
        {formatTranslation(intl, CommonTranslationKeys.publish)}
      </MemoButtonContained>
    </>
  );

  return isMobile ? mobileButton : desktopButtons;
}
